/**
 * Phase 3 Feature J — compare KB-scored candidates vs LLM-ranked DDx ordering.
 */

import { TRUST_TIERS, VERIFICATION_STATUS } from './ddxTemporalTaxonomy.js';

/**
 * @typedef {Object} LlmRankedDdx
 * @property {number} rank - 0-based position in LLM list
 * @property {string} [diagnosisId]
 * @property {string} [label]
 */

/**
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
function labelsLooselyMatch(a, b) {
  const x = String(a || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const y = String(b || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  if (!x || !y) return false;
  if (x === y) return true;
  if (x.includes(y) || y.includes(x)) return true;
  const wx = x.split(/\s+/).filter((w) => w.length > 3);
  const wy = y.split(/\s+/).filter((w) => w.length > 3);
  return wx.some((w) => wy.includes(w));
}

/**
 * @param {import('./ddxTypes.js').ScoredDiagnosis | object} kb
 * @param {LlmRankedDdx} llm
 * @returns {boolean}
 */
function sameDiagnosis(kb, llm) {
  if (llm.diagnosisId && kb.diagnosisId && String(llm.diagnosisId) === String(kb.diagnosisId)) return true;
  return labelsLooselyMatch(kb.label || '', llm.label || '');
}

/**
 * @param {import('./ddxTypes.js').ScoredDiagnosis[]|object[]} kbCandidates - sorted by KB score desc
 * @param {LlmRankedDdx[]} llmRankedDdx
 * @returns {import('./ddxTypes.js').TrustConflict[]}
 */
export function detectTrustConflicts(kbCandidates, llmRankedDdx) {
  const kb = [...(kbCandidates || [])].sort((a, b) => (b.adjustedScore || 0) - (a.adjustedScore || 0));
  /** @type {import('./ddxTypes.js').TrustConflict[]} */
  const out = [];

  const getLlmListRank = (dx) => {
    const found = (llmRankedDdx || []).find((l) => sameDiagnosis(dx, l));
    return found ? found.rank : 999;
  };

  for (let i = 0; i < kb.length; i += 1) {
    const c = kb[i];
    const kbR = i;
    const llmR = getLlmListRank(c);
    const t1v =
      Number(c.trustTier) === TRUST_TIERS.TIER_1 &&
      (c.verificationStatus === VERIFICATION_STATUS.VERIFIED || !c.verificationStatus);
    const t2u =
      Number(c.trustTier) === TRUST_TIERS.TIER_2 && c.verificationStatus === VERIFICATION_STATUS.UNVERIFIED;

    if (t1v && (c.adjustedScore || 0) > 0.6 && llmR > kbR + 3) {
      out.push({
        conflictType: 'TIER1_DEMOTED',
        diagnosisId: String(c.diagnosisId || ''),
        diagnosisLabel: String(c.label || c.diagnosisId || ''),
        kbRank: kbR,
        llmRank: llmR,
        kbScore: c.adjustedScore || 0,
        severity: llmR >= 8 ? 'critical' : 'warning',
      });
    }

    if (t2u && llmR < kbR - 2 && (c.adjustedScore || 0) < 0.5) {
      out.push({
        conflictType: 'TIER2_PROMOTED',
        diagnosisId: String(c.diagnosisId || ''),
        diagnosisLabel: String(c.label || c.diagnosisId || ''),
        kbRank: kbR,
        llmRank: llmR,
        kbScore: c.adjustedScore || 0,
        severity: 'warning',
      });
    }

    if (c.isCantMiss && llmR >= 5) {
      out.push({
        conflictType: 'CANT_MISS_BURIED',
        diagnosisId: String(c.diagnosisId || ''),
        diagnosisLabel: String(c.label || c.diagnosisId || ''),
        kbRank: kbR,
        llmRank: llmR,
        kbScore: c.adjustedScore || 0,
        severity: 'critical',
      });
    }
  }

  const seen = new Set();
  return out.filter((x) => {
    const k = `${x.conflictType}|${x.diagnosisId}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}
